import React, { useState, useEffect } from 'react';
import { Save, Wand2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import toast from 'react-hot-toast';

interface AISettings {
  caption_prompt_template: string;
  content_gaps_prompt_template: string;
  title_prompt_template: string;
}

const defaultSettings: AISettings = {
  caption_prompt_template: `I have a video transcript, and I need you to create a professional and engaging LinkedIn post caption based on it. The caption should:

Use 2-3 short, attention-grabbing bullet points with emojis at the start to highlight the key themes or benefits of the content.
Include a bold and compelling headline directly relevant to the content, without explicitly labeling it as 'Headline.'
Craft an engaging and conversational introduction that sets the context for the post, without using labels like 'Introduction.'
Highlight key takeaways or features in a numbered list (3-5 points). Each point should be concise and formatted with plain text and emojis, avoiding asterisks or unnecessary markdown-like formatting.
Conclude with an engaging question or call to action, without labeling it as 'Conclusion.'
{{includeHashtags ? 'Include 5-8 relevant hashtags at the end.' : ''}}

Length guideline: {{length === 'short' ? 'Keep it concise' : length === 'medium' ? 'Balanced length' : 'More detailed'}}
Tone: {{tone}}
{{customInstructions ? 'Additional instructions: ' + customInstructions : ''}}

Ensure the final caption is visually appealing, easy to read, and free of section labels or markdown formatting. The tone should be professional yet relatable, tailored to LinkedIn audiences.

Here's the transcript: "{{transcript}}"`,
  content_gaps_prompt_template: `Analyze this video transcript and identify 5 potential content gaps or related topics that could be covered in future videos. For each gap, provide a title and brief description.

Transcript: "{{transcript}}"

Format your response as a JSON array of objects, each with 'title' and 'description' properties. Example:
[
  {
    "title": "Topic Title",
    "description": "Brief description of the content gap"
  }
]`,
  title_prompt_template: `You are a content specialist who creates concise, engaging titles for video content. Generate a short, descriptive title (maximum 60 characters) that captures the main theme of the video based on its transcript.

Generate a short, engaging title for this video transcript: "{{transcript}}"`
};

export function AISettings() {
  const [settings, setSettings] = useState<AISettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_settings')
        .select('*')
        .eq('id', 1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // Not found error
          await saveSettings(defaultSettings); // Initialize with defaults
        } else {
          throw error;
        }
      } else if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      setError('Failed to load AI settings');
    }
  };

  const saveSettings = async (newSettings: AISettings) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('ai_settings')
        .upsert({
          id: 1,
          ...newSettings
        });

      if (error) throw error;
      toast.success('AI settings saved successfully');
      setSettings(newSettings);
    } catch (error) {
      console.error('Error saving AI settings:', error);
      setError('Failed to save AI settings');
      toast.error('Failed to save AI settings');
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    if (confirm('Are you sure you want to reset all prompts to their default values?')) {
      setSettings(defaultSettings);
      saveSettings(defaultSettings);
    }
  };

  return (
    <div className="bg-card shadow rounded-lg p-6 border">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-foreground">AI Configuration</h3>
        <button
          onClick={resetToDefaults}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Reset to defaults
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-destructive/10 border-l-4 border-destructive p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div className="ml-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Wand2 className="w-5 h-5 text-primary" />
            <h4 className="font-medium text-foreground">Caption Generation</h4>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Caption Prompt Template
            </label>
            <p className="text-sm text-muted-foreground mb-2">
              Available variables: {'{{transcript}}, {{tone}}, {{length}}, {{includeHashtags}}, {{customInstructions}}'}
            </p>
            <textarea
              value={settings.caption_prompt_template}
              onChange={(e) => setSettings({ ...settings, caption_prompt_template: e.target.value })}
              rows={15}
              className="w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary font-mono text-sm bg-background text-foreground"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Wand2 className="w-5 h-5 text-primary" />
            <h4 className="font-medium text-foreground">Content Gaps Analysis</h4>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Content Gaps Prompt Template
            </label>
            <p className="text-sm text-muted-foreground mb-2">
              Available variables: {'{{transcript}}'}
            </p>
            <textarea
              value={settings.content_gaps_prompt_template}
              onChange={(e) => setSettings({ ...settings, content_gaps_prompt_template: e.target.value })}
              rows={10}
              className="w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary font-mono text-sm bg-background text-foreground"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Wand2 className="w-5 h-5 text-primary" />
            <h4 className="font-medium text-foreground">Title Generation</h4>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Title Prompt Template
            </label>
            <p className="text-sm text-muted-foreground mb-2">
              Available variables: {'{{transcript}}'}
            </p>
            <textarea
              value={settings.title_prompt_template}
              onChange={(e) => setSettings({ ...settings, title_prompt_template: e.target.value })}
              rows={5}
              className="w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary font-mono text-sm bg-background text-foreground"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => saveSettings(settings)}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}